<script>
  import { createEventDispatcher } from "svelte"

  export let customFieldsPath
  export let palette

  // Below this movement a press is a click (keyboard/tap add), above it a drag.
  const DRAG_THRESHOLD_PX = 4

  const dispatch = createEventDispatcher()

  let drag = null

  function pointercancel() {
    drag = null
  }

  function click(event, field) {
    // Pointer clicks are handled on pointerup so a press can become a drag.
    // Keyboard and assistive-technology clicks have no pointer detail.
    if (event.detail === 0) dispatch("add", { field })
  }

  function pointerdown(event, field) {
    if (event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    drag = {
      field,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      x: event.clientX,
      y: event.clientY,
    }
  }

  function pointermove(event) {
    if (!drag) return
    drag = {
      ...drag,
      moved:
        drag.moved ||
        Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) >= DRAG_THRESHOLD_PX,
      x: event.clientX,
      y: event.clientY,
    }
  }

  function pointerup(event) {
    if (!drag) return
    const { field, moved } = drag
    drag = null
    if (moved) {
      dispatch("drop", { clientX: event.clientX, clientY: event.clientY, field })
    } else {
      dispatch("add", { field })
    }
  }
</script>

<aside class="border-b lg:border-b-0 lg:border-r lg:w-72 shrink-0 w-full xl:w-80">
  <div class="divide-y lg:max-h-[80vh] lg:overflow-y-auto">
    {#each palette as group, index (group.label)}
      <details class="group/palette" open={index === 0}>
        <summary
          class="[&::-webkit-details-marker]:hidden cursor-pointer flex gap-2 hover:bg-slate-100 items-center justify-between list-none p-3 text-sm transition-colors"
        >
          <span class="font-bold">{group.label}</span>
          <span class="flex gap-2 items-center">
            <span class="bg-slate-200 px-2 py-0.5 rounded-full text-slate-700 text-xs">
              {group.fields.length}
            </span>
            <svg
              aria-hidden="true"
              class="group-open/palette:rotate-180 size-4 transition-transform"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              viewBox="0 0 24 24"
            >
              <path
                d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </summary>

        <div class="border-t divide-y">
          {#each group.fields as field (field.key)}
            <button
              class="block cursor-grab hover:bg-slate-50 p-3 select-none text-left touch-pan-y transition-colors w-full"
              title="Drag {field.label} onto a page"
              type="button"
              on:click={(event) => click(event, field)}
              on:pointercancel={pointercancel}
              on:pointerdown={(event) => pointerdown(event, field)}
              on:pointermove={pointermove}
              on:pointerup={pointerup}
            >
              <span class="flex flex-col min-w-0">
                <span class="font-medium text-sm">{field.label}</span>
                <span class="text-slate-500 text-xs truncate">
                  Ex: {field.example?.toString() || "No preview value"}
                </span>
              </span>
            </button>
          {/each}

          {#if group.label === "Custom Fields" && customFieldsPath}
            <div class="p-3">
              <a class="link text-sm" href={customFieldsPath}>Edit Custom Fields</a>
            </div>
          {/if}
        </div>
      </details>
    {/each}
  </div>
</aside>

{#if drag?.moved}
  <!-- Centered on the cursor because the drop also centers the new box there. -->
  <div
    class="-translate-x-1/2 -translate-y-1/2 bg-sky-100 border border-sky-500 fixed pointer-events-none px-2 py-1 rounded shadow text-sky-950 text-sm z-50"
    style="left: {drag.x}px; top: {drag.y}px;"
  >
    {drag.field.label}
  </div>
{/if}
